"use client";

import React, { useState } from "react";
import { ArrowRight, CircleCheck as CheckCircle2, Code as Code2, Cpu, Gauge, Sparkles, CircleX as XCircle, Zap } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const benefitTiles = [{
  id: "codebase",
  title: "Zero-bloat codebase",
  icon: Code2,
  tagline: "Ultra-lean architecture for maximum crawler efficiency",
  description: "We strip away legacy framework bloat and unnecessary scripts. ClearLine builds lean, high-performing websites engineered specifically for instant indexing by AI search bots and conventional search engines.",
  stats: ["0ms Main-thread delay", "99+ Lighthouse Score", "Clean Semantic HTML"]
}, {
  id: "crawler",
  title: "AI crawler priority",
  icon: Cpu,
  tagline: "Structured for ChatGPT, Claude, and Perplexity understanding",
  description: "Deep JSON-LD entity graph schema ensures LLMs accurately extract and synthesize your service offerings, converting search queries into direct citations and authoritative answers.",
  stats: ["100% Schema Validation", "Instant LLM Ingestion", "Verified Entity Citations"]
}, {
  id: "conversion",
  title: "Built-in conversion engine",
  icon: Zap,
  tagline: "Seamless GoHighLevel lead flow and direct booking",
  description: "Frictionless booking workflows integrated directly into your site structure. Turn high-intent AI search traffic into direct scheduled calls and verified local leads without leakages.",
  stats: ["3x Higher Lead Capture", "Instant Calendar Sync", "Automated SMS Nurture"]
}, {
  id: "speed",
  title: "Speed guarantee",
  icon: Gauge,
  tagline: "Sub-second mobile performance across all edge networks",
  description: "If your site doesn't load in under 1 second on mobile devices, we optimize until it does. Uncompromising page speed is the ultimate prerequisite for ranking in modern generative search engines.",
  stats: ["<0.8s LCP Speed Target", "100 Mobile Performance", "Global CDN Distribution"]
}];
const comparisonData = [{
  feature: "Average Mobile Load Time",
  typical: "3.8s to 5.2s",
  clearline: "0.6s to 0.9s"
}, {
  feature: "Mobile PageSpeed Score",
  typical: "45 - 65 / 100",
  clearline: "98 - 100 / 100"
}, {
  feature: "Structured Schema Coverage",
  typical: "Basic / Fragmented",
  clearline: "Full JSON-LD Entity Graph"
}, {
  feature: "AI Search Citability",
  typical: "Unindexed / Hallucinated",
  clearline: "Direct Citation Priority"
}];
export default function Benefits() {
  const [selectedId, setSelectedId] = useState<string>("codebase");
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.15
  });
  const selectedBenefit = benefitTiles.find(tile => tile.id === selectedId) || benefitTiles[0];
  return <section data-section-id="31005" id="interactivebenefitsdetail" ref={sectionRef} className={`relative w-full bg-background py-24 md:py-36 transition-opacity duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-12 text-center md:mb-16">
          <Badge variant="outline" className="mb-4 border-[var(--accent-deep-red)]/30 bg-[var(--accent-deep-red)]/10 text-foreground px-3 py-1">
            <Sparkles className="mr-1.5 size-3.5 text-[var(--accent-deep-red)]" />
            ClearLine Engineering Standard
          </Badge>

          <h2 className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl">
            Built for performance, engineered for{" "}
            <span className="italic text-[var(--accent-deep-red)] font-serif">
              visibility
            </span>
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base text-muted-foreground sm:text-lg">
            Explore how our zero-bloat approach ensures your business stays ahead in both standard search engines and AI recommendations.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:items-start">
          <div className="block lg:hidden">
            <ScrollArea className="w-full whitespace-nowrap pb-2">
              <div className="flex gap-3">
                {benefitTiles.map((tile, i) => {
                const Icon = tile.icon;
                const isActive = selectedId === tile.id;
                return <Button key={tile.id} data-index={i} variant="outline" onClick={() => setSelectedId(tile.id)} className={`h-auto flex-shrink-0 rounded-xl px-4 py-3 text-left transition-all duration-200 ${isActive ? "border-[var(--accent-deep-red)] bg-[#141419] text-foreground shadow-[0_0_15px_rgba(139,26,43,0.25)]" : "border-border bg-[hsl(240_10%_7%)] text-muted-foreground hover:border-[var(--accent-deep-red)]/50 hover:text-foreground"}`}>
                      <div className="flex items-center gap-2">
                        <Icon className={`size-4 ${isActive ? "text-[var(--accent-deep-red)]" : "text-muted-foreground"}`} />
                        <span className="font-medium text-sm">
                          {tile.title}
                        </span>
                      </div>
                    </Button>;
              })}
              </div>
              <ScrollBar orientation="horizontal" className="pt-2" />
            </ScrollArea>
          </div>

          <div className="hidden lg:col-span-5 lg:flex lg:flex-col lg:gap-4">
            {benefitTiles.map((tile, i) => {
            const Icon = tile.icon;
            const isActive = selectedId === tile.id;
            return <Card key={tile.id} data-index={i} onClick={() => setSelectedId(tile.id)} className={`group cursor-pointer rounded-2xl border bg-[hsl(240_10%_7%)] p-5 transition-all duration-200 ${isActive ? "border-[var(--accent-deep-red)] shadow-[0_0_20px_rgba(139,26,43,0.3)] translate-x-1" : "border-border hover:border-[var(--accent-deep-red)]/50 hover:bg-[hsl(240_10%_9%)]"}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`flex size-10 items-center justify-center rounded-xl transition-colors ${isActive ? "bg-[var(--accent-deep-red)]/20 text-[var(--accent-deep-red)]" : "bg-muted text-muted-foreground group-hover:text-foreground"}`}>
                        <Icon className="size-5" />
                      </div>
                      <div>
                        <CardTitle className="font-default text-base font-semibold text-foreground">
                          {tile.title}
                        </CardTitle>
                        <p className="line-clamp-1 text-xs text-muted-foreground">
                          {tile.tagline}
                        </p>
                      </div>
                    </div>
                    <ArrowRight className={`size-4 transition-transform ${isActive ? "translate-x-1 text-[var(--accent-deep-red)]" : "text-muted-foreground opacity-0 group-hover:opacity-100"}`} />
                  </div>
                </Card>;
          })}
          </div>

          <div className="lg:col-span-7">
            <Card className="rounded-2xl border border-border bg-[hsl(240_10%_7%)] p-6 md:p-8 shadow-xl">
              <CardHeader className="p-0 pb-6">
                <div className="flex items-center gap-3">
                  <div className="flex size-12 items-center justify-center rounded-xl bg-[var(--accent-deep-red)]/20 text-[var(--accent-deep-red)]">
                    {React.createElement(selectedBenefit.icon, {
                    className: "size-6"
                  })}
                  </div>
                  <div>
                    <CardTitle className="font-default text-xl font-bold text-foreground md:text-2xl">
                      {selectedBenefit.title}
                    </CardTitle>
                    <p className="text-sm text-[var(--accent-deep-red)] font-medium mt-0.5">
                      {selectedBenefit.tagline}
                    </p>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="p-0 space-y-6">
                <p className="text-base text-muted-foreground leading-relaxed">
                  {selectedBenefit.description}
                </p>

                <Separator className="bg-border/60" />

                <div>
                  <h4 className="font-default text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                    Key Deliverables & Performance Targets
                  </h4>
                  <div className="flex flex-wrap gap-2.5">
                    {selectedBenefit.stats.map((stat, i) => <Badge key={i} data-index={i} variant="outline" className="rounded-lg border-[var(--accent-deep-red)]/40 bg-[var(--accent-deep-red)]/10 px-3 py-1.5 text-xs font-medium text-foreground">
                        <CheckCircle2 className="mr-1.5 size-3.5 text-[var(--accent-deep-red)]" />
                        {stat}
                      </Badge>)}
                  </div>
                </div>

                <div className="pt-2">
                  <Button asChild variant="default" className="w-full sm:w-auto bg-[var(--accent-deep-red)] text-white hover:bg-[var(--accent-deep-red)]/90">
                    <Link to="/PricingPlansPage"></Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="mt-16 rounded-2xl border border-border bg-[hsl(240_10%_7%)] p-6 md:p-8">
          <div className="mb-6 flex flex-col justify-between gap-2 sm:flex-row sm:items-center">
            <div>
              <h3 className="font-default text-lg font-bold text-foreground">
                Performance Comparison
              </h3>
              <p className="text-xs text-muted-foreground">
                How traditional agency builds stack up against ClearLine GEO optimization
              </p>
            </div>
            <Badge variant="outline" className="w-fit border-border bg-background text-xs text-muted-foreground">
              Real-world benchmark data
            </Badge>
          </div>

          <div className="grid grid-cols-1 gap-3">
            <div className="hidden grid-cols-12 rounded-lg bg-muted/30 p-3 text-xs font-semibold text-muted-foreground sm:grid">
              <div className="col-span-5">Metric / Feature</div>
              <div className="col-span-3">Typical Agency Site</div>
              <div className="col-span-4 text-right sm:text-left">
                ClearLine Build
              </div>
            </div>

            {comparisonData.map((row, i) => <div key={i} data-index={i} className="grid grid-cols-1 items-center gap-2 rounded-xl border border-border/50 bg-background/50 p-3.5 text-sm sm:grid-cols-12 sm:gap-4">
                <div className="col-span-1 font-medium text-foreground sm:col-span-5">
                  {row.feature}
                </div>

                <div className="col-span-1 flex items-center text-xs text-muted-foreground sm:col-span-3 sm:text-sm">
                  <XCircle className="mr-1.5 size-4 shrink-0 text-destructive/70 sm:hidden" />
                  <span className="sm:hidden font-semibold mr-1">Typical:</span>
                  {row.typical}
                </div>

                <div className="col-span-1 flex items-center font-semibold text-[var(--accent-deep-red)] sm:col-span-4">
                  <CheckCircle2 className="mr-1.5 size-4 shrink-0 text-[var(--accent-deep-red)] sm:hidden" />
                  <span className="sm:hidden font-semibold mr-1">ClearLine:</span>
                  {row.clearline}
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}